import { api, ApiError, unwrapObject } from "@/lib/api";
import { announceVisitChange, type Visit } from "@/lib/visits";

/**
 * Check-ins and check-outs made with no signal (a mill or a village shop) are kept on the phone with the time they
 * happened, and sent in order once the phone is back online. The server accepts them up to a day late and marks the
 * visit as sent later. Only this browser sees the queue until it is sent.
 */
const KEY = "visit-queue";
export const LOCAL_PREFIX = "local:";

export interface QueuedCheckIn {
  kind: "checkin";
  localId: string;
  label: string;
  captured_at: string;
  body: Record<string, unknown>;
  /** Set once the server has it */
  serverId?: string;
}
export interface QueuedCheckOut {
  kind: "checkout";
  /** The visit: a server id, or the local id of a check-in still in the queue */
  visitId: string;
  label: string;
  captured_at: string;
  body: Record<string, unknown>;
}
type Item = QueuedCheckIn | QueuedCheckOut;

const read = (): Item[] => {
  try {
    return JSON.parse(localStorage.getItem(KEY) || "[]");
  } catch {
    return [];
  }
};
const write = (items: Item[]) => {
  try {
    if (items.length) localStorage.setItem(KEY, JSON.stringify(items));
    else localStorage.removeItem(KEY);
  } catch {
    /* storage blocked: nothing more we can do */
  }
};

/** No connection, or the server couldn't be reached (as opposed to the server refusing the request) */
export const isOffline = (e: unknown) =>
  (typeof navigator !== "undefined" && navigator.onLine === false) || !(e instanceof ApiError) || [0, 502, 503, 504].includes((e as ApiError).status);

export const pendingCount = () => read().length;

/** The visit the rep checked in to offline and hasn't checked out of yet, shown on the "On visit" bar */
export function pendingVisit(): Visit | null {
  const items = read();
  const open = [...items].reverse().find((i): i is QueuedCheckIn => i.kind === "checkin" && !items.some((o) => o.kind === "checkout" && o.visitId === i.localId));
  if (!open) return null;
  return {
    id: `${LOCAL_PREFIX}${open.localId}`,
    owner: { id: "", name: null },
    related_type: (open.body.related_type as any) || "ACCOUNT",
    related_id: (open.body.related_id as string) || "",
    related_label: open.label,
    deal_id: (open.body.deal_id as string) || null,
    deal_label: null,
    task_id: null,
    visit_status: "CHECKED_IN",
    planned_at: null,
    purpose: (open.body.purpose as string) || null,
    check_in_at: open.captured_at,
    check_in_lat: (open.body.lat as number) ?? null,
    check_in_lng: (open.body.lng as number) ?? null,
    check_in_accuracy_m: (open.body.accuracy as number) ?? null,
    check_in_distance_m: null,
    location_flag: null,
    check_out_at: null,
    check_out_lat: null,
    check_out_lng: null,
    duration_minutes: null,
    auto_closed: false,
    offline: true,
    outcome: null,
    notes: null,
    next_follow_up_at: null,
    has_photo: false,
    activity_id: null,
    created_on: open.captured_at,
    can_change: true,
  };
}

export function queueCheckIn(body: Record<string, unknown>, label: string): Visit {
  const localId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  write([...read(), { kind: "checkin", localId, label, captured_at: new Date().toISOString(), body }]);
  announceVisitChange();
  return pendingVisit()!;
}

export function queueCheckOut(visitId: string, body: Record<string, unknown>, label: string) {
  const id = visitId.startsWith(LOCAL_PREFIX) ? visitId.slice(LOCAL_PREFIX.length) : visitId;
  write([...read(), { kind: "checkout", visitId: id, label, captured_at: new Date().toISOString(), body }]);
  announceVisitChange();
}

let flushing: Promise<{ sent: number; failed: string[] }> | null = null;

/** Sends what is waiting, oldest first; stops at the first connection failure and keeps the rest */
export function flushVisitQueue() {
  flushing ??= (async () => {
    let sent = 0;
    const failed: string[] = [];
    try {
      for (;;) {
        const items = read();
        const next = items[0];
        if (!next) break;
        try {
          if (next.kind === "checkin") {
            const v = unwrapObject<Visit>(await api.post("/api/FieldVisit/CheckIn", { ...next.body, captured_at: next.captured_at }));
            // Later check-outs of this visit now go to the server's id
            write(items.slice(1).map((i) => (i.kind === "checkout" && i.visitId === next.localId ? { ...i, visitId: v.id } : i)));
          } else {
            if (/^\d+-/.test(next.visitId)) {
              // Its check-in was refused and dropped: nothing to check out of
              write(items.slice(1));
              continue;
            }
            await api.post(`/api/FieldVisit/CheckOut/${next.visitId}`, { ...next.body, captured_at: next.captured_at });
            write(items.slice(1));
          }
          sent++;
        } catch (e) {
          if (isOffline(e)) break;
          // The server refused it (too old, closed deal…): drop it so the rest can go, and tell the rep
          failed.push(`${next.kind === "checkin" ? "Check-in" : "Check-out"} at ${next.label}: ${(e as Error).message}`);
          write(items.slice(1));
        }
      }
    } finally {
      flushing = null;
    }
    if (sent || failed.length) announceVisitChange();
    return { sent, failed };
  })();
  return flushing;
}
